import { ActionIcon } from "@taunggyi-plus/design-system";
import { useDetailRecord, useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { RECORDS_PER_PAGE, ServerPagination, StatusPill } from "@taunggyi-plus/design-system";
import { AdminDeliveryQueue } from "@taunggyi-plus/onboarding-feature";

type Order = {
  service: string;
  id: string;
  customer: string;
  provider: string;
  contact: string;
  detail: string;
  status: string;
  created_at: string;
};
type OrderPage = {
  items: Order[];
  total: number;
  counts: Record<string, number> | null;
  statuses: string[];
};
const services: Record<string, string> = {
  all: "All services",
  hotel: "Hotel bookings",
  merchant: "Food orders",
  delivery: "Delivery",
  taxi: "Taxi rides",
};
const label = (value: string) =>
  value.replaceAll("_", " ").replace(/^./, (char) => char.toUpperCase());
export function AdminOrders() {
  const { client } = useAuth();
  const [data, setData] = useState<OrderPage | null>(null);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({ service: "all", status: "all" });
  const [draft, setDraft] = useState(filters);
  const [draftQuery, setDraftQuery] = useState("");
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useFeedback("", "error");
  const [selected, setSelected] = useDetailRecord(data?.items ?? [], "order");
  const [dispatch, setDispatch] = useState(false);
  const filterDialog = useRef<HTMLDialogElement>(null);
  const sequence = useRef({ value: 0 });
  const load = useCallback(async () => {
    if (!client) return;
    const run = ++sequence.current.value;
    setBusy(true);
    try {
      const result = await client.rpc("admin_orders_page", {
        target_service: filters.service,
        target_status: filters.status,
        target_query: query,
        target_page: page,
      });
      if (run !== sequence.current.value) return;
      if (result.error) throw new Error(result.error.message);
      const next = result.data as OrderPage;
      if (page > 0 && page * RECORDS_PER_PAGE >= next.total) {
        setPage(Math.max(0, Math.ceil(next.total / RECORDS_PER_PAGE) - 1));
        return;
      }
      setData((old) => {
        return page === 0
          ? next
          : {
              ...next,
              items: [
                ...(old?.items ?? []).filter((o) => !next.items.some((n) => n.id === o.id)),
                ...next.items,
              ],
            };
      });
      setError("");
    } catch (e) {
      if (run === sequence.current.value) {
        setData(null);
        setError(e instanceof Error ? e.message : "Orders could not be loaded.");
      }
    } finally {
      if (run === sequence.current.value) setBusy(false);
    }
  }, [client, filters.service, filters.status, page, query, setError]);
  useEffect(() => {
    const lifecycle = sequence.current;
    const timer = window.setTimeout(() => void load(), 200);
    const refresh = window.setInterval(() => {
      if (!document.hidden) void load();
    }, 30000);
    return () => {
      lifecycle.value++;
      window.clearTimeout(timer);
      window.clearInterval(refresh);
    };
  }, [load]);
  const reset = () => {
    setFilters({ service: "all", status: "all" });
    setQuery("");
    setPage(0);
  };
  if (selected)
    return (
      <article className="record-detail-page" aria-labelledby="admin-order-detail">
        <header>
          <h2 id="admin-order-detail">Order details</h2>
          <button
            type="button"
            className="button button--secondary"
            aria-label="Close details"
            onClick={() => setSelected(null)}
          >
            ×
          </button>
        </header>
        {selected && (
          <>
            <dl className="admin-order-detail">
              {[
                ["Order ID", selected.id],
                ["Service", services[selected.service]],
                ["Customer", selected.customer],
                ["Provider", selected.provider],
                ["Provider contact", selected.contact],
                ["Status", label(selected.status)],
                ["Request", selected.detail],
                [
                  "Requested",
                  new Date(selected.created_at).toLocaleString(undefined, {
                    timeZone: "Asia/Yangon",
                  }),
                ],
              ].map(([key, value]) => (
                <div key={key}>
                  <dt>{key}</dt>
                  <dd>{value || "Not provided"}</dd>
                </div>
              ))}
            </dl>
            <p>
              The provider manages this order. Use fleet assignment for deliveries waiting for a
              fleet.
            </p>
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setSelected(null)}
            >
              Close
            </button>
          </>
        )}
      </article>
    );
  return (
    <section className="admin-orders-workspace">
      <div className="subscription-table-toolbar">
        <p>Bookings and orders across all services.</p>
        <button
          type="button"
          className="button button--secondary"
          aria-expanded={dispatch}
          onClick={() => setDispatch(!dispatch)}
        >
          {dispatch ? "Back to all orders" : "Assign delivery fleets"}
        </button>
      </div>
      {dispatch ? (
        <AdminDeliveryQueue />
      ) : (
        <>
          <div className="subscription-table-toolbar">
            <button
              type="button"
              className="toolbar-icon-button"
              aria-label="Filter orders"
              title="Filter orders"
              aria-haspopup="dialog"
              data-filtered={filters.service !== "all" || filters.status !== "all"}
              onClick={() => {
                setDraftQuery(query);
                setDraft(filters);
                filterDialog.current?.showModal();
              }}
            >
              <ActionIcon action="filter" />
            </button>
            <button
              type="button"
              className="button button--secondary"
              disabled={busy}
              onClick={() => void load()}
            >
              Refresh
            </button>
          </div>
          <div className="subscription-filter-summary" aria-live="polite">
            <span>
              {services[filters.service]} ·{" "}
              {filters.status === "all" ? "All statuses" : label(filters.status)} ·{" "}
              {busy ? "Loading…" : `${data?.total ?? 0} records`}
            </span>
            {(query || filters.service !== "all" || filters.status !== "all") && (
              <button type="button" onClick={reset}>
                Clear filters
              </button>
            )}
          </div>
          {error && <p role="alert">{error}</p>}
          <div
            className="subscription-table-scroll"
            role="region"
            aria-label="Orders table"
            tabIndex={0}
            aria-busy={busy}
          >
            <table className="subscription-table">
              <caption className="visually-hidden">All service orders, newest first</caption>
              <thead>
                <tr>
                  <th scope="col">Customer / order</th>
                  <th scope="col">Service / provider</th>
                  <th scope="col">Status</th>
                  <th scope="col">Requested</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>
                {!busy &&
                  data?.items.map((order) => (
                    <tr key={`${order.service}:${order.id}`}>
                      <th scope="row">
                        {order.customer || "Customer"}
                        <small>{order.id.slice(0, 8)}</small>
                      </th>
                      <td data-label="Service / provider" className="admin-order-service">
                        {services[order.service]}
                        <small>{order.provider}</small>
                      </td>
                      <td data-label="Status">
                        <StatusPill
                          tone={
                            ["completed", "delivered", "confirmed"].includes(order.status)
                              ? "success"
                              : ["pending", "unassigned", "quoted"].includes(order.status)
                                ? "warning"
                                : "neutral"
                          }
                        >
                          {label(order.status)}
                        </StatusPill>
                      </td>
                      <td data-label="Requested">
                        {new Date(order.created_at).toLocaleString(undefined, {
                          timeZone: "Asia/Yangon",
                        })}
                      </td>
                      <td className="admin-order-action">
                        <button
                          className="button button--secondary"
                          type="button"
                          aria-label={`View order ${order.id}`}
                          onClick={() => {
                            setSelected(order);
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                {(busy || !data?.items.length) && (
                  <tr>
                    <td colSpan={5}>
                      {busy
                        ? "Loading orders…"
                        : error
                          ? "Orders are unavailable. Try Refresh."
                          : query || filters.status !== "all"
                            ? "No orders match these filters."
                            : "No orders yet for this service."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <ServerPagination page={page} total={data?.total ?? 0} onPage={setPage} loading={busy} />
        </>
      )}
      <dialog
        ref={filterDialog}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.currentTarget.close();
          }
        }}
        className="customer-order-filter-dialog subscription-popup"
        aria-labelledby="admin-order-filters"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(draftQuery);
            setFilters(draft);
            setPage(0);
            filterDialog.current?.close();
          }}
        >
          <header>
            <h2 id="admin-order-filters">Filter orders</h2>
            <button
              type="button"
              className="button button--secondary"
              aria-label="Close filters"
              onClick={() => filterDialog.current?.close()}
            >
              ×
            </button>
          </header>
          <label className="field">
            <span className="visually-hidden">Search orders</span>
            <input
              type="search"
              placeholder="Search customer, provider or order ID"
              maxLength={200}
              value={draftQuery}
              onChange={(e) => {
                setDraftQuery(e.target.value);
              }}
            />
          </label>
          <label className="field">
            <span>Service</span>
            <select
              aria-label="Service"
              value={draft.service}
              onChange={(e) => setDraft({ service: e.target.value, status: "all" })}
            >
              {Object.entries(services).map(([key, name]) => (
                <option value={key} key={key}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select
              aria-label="Status"
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value })}
            >
              <option value="all">All statuses</option>
              {Array.from(
                new Set([
                  ...(data?.statuses ?? []),
                  "pending",
                  "quoted",
                  "accepted",
                  "preparing",
                  "ready",
                  "confirmed",
                  "checked_in",
                  "assigned",
                  "unassigned",
                  "in_progress",
                  "picked_up",
                  "completed",
                  "delivered",
                  "cancelled",
                  "rejected",
                ]),
              )
                .sort()
                .map((status) => (
                  <option key={status} value={status}>
                    {label(status)}
                  </option>
                ))}
            </select>
          </label>
          <div className="subscription-popup-actions">
            <button
              className="button button--secondary"
              type="button"
              onClick={() => setDraft({ service: "all", status: "all" })}
            >
              Reset
            </button>
            <button className="button button--primary">Apply filters</button>
          </div>
        </form>
      </dialog>
    </section>
  );
}
